import { oc, eventIterator } from "@orpc/contract";
import { z } from "zod";

/**
 * Module Key Schema - modules the foreground agent can operate in
 */
export const ModuleKeySchema = z.enum([
  "ap",
  "ar",
  "home",
  "oms",
  "portal",
  "procurement",
  "rebates",
  "sales-orders",
]);

/**
 * Message Schema - conversation messages matching AI SDK pattern
 */
const MessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string(),
});

/**
 * Foreground Agent Chat Input Schema
 */
export const ForegroundAgentChatInputSchema = z.object({
  moduleKey: ModuleKeySchema,
  currentPage: z.string(),
  messages: z.array(MessageSchema),
  threadId: z.string().uuid().optional(),
});

/**
 * Finish Reason Schema - reasons why the stream ended
 */
export const FinishReasonSchema = z.enum([
  "stop",
  "length",
  "tool-calls",
  "content-filter",
  "error",
  "other",
]);

/**
 * Agent Stream Event Schema - discriminated union with 6 event types
 */
export const AgentStreamEventSchema = z.discriminatedUnion("type", [
  // Text delta event - streamed text chunks
  z.object({
    type: z.literal("text-delta"),
    text: z.string(),
  }),
  // Tool call event - when agent invokes a tool
  z.object({
    type: z.literal("tool-call"),
    toolCallId: z.string(),
    toolName: z.string(),
    args: z.record(z.string(), z.unknown()),
  }),
  // Tool result event - result from tool execution
  z.object({
    type: z.literal("tool-result"),
    toolCallId: z.string(),
    toolName: z.string(),
    result: z.unknown(),
  }),
  // Thread info event - thread metadata
  z.object({
    type: z.literal("thread-info"),
    threadId: z.string().uuid(),
    isNew: z.boolean(),
  }),
  // Finish event - stream completed
  z.object({
    type: z.literal("finish"),
    finishReason: FinishReasonSchema,
  }),
  // Error event - something went wrong
  z.object({
    type: z.literal("error"),
    message: z.string(),
    code: z.string().optional(),
  }),
]);

/**
 * Foreground Agent Contracts
 */
export const foregroundAgentContracts = {
  chat: oc
    .input(ForegroundAgentChatInputSchema)
    .output(eventIterator(AgentStreamEventSchema))
    .route({
      method: "POST",
      path: "/foreground-agent/chat",
    }),
};

// Export types
export type ModuleKey = z.infer<typeof ModuleKeySchema>;
export type ForegroundAgentChatInput = z.infer<
  typeof ForegroundAgentChatInputSchema
>;
export type FinishReason = z.infer<typeof FinishReasonSchema>;
export type AgentStreamEvent = z.infer<typeof AgentStreamEventSchema>;
